import React, { useState, useEffect, useCallback, useRef } from "react";
import ClientPicker from "../invoice/ClientPicker";
import DocTypeSelector from "../invoice/DocTypeSelector";
import { BOLETA, FACTURA, VALE } from "../../Global";

export function IdentifyClient({ onChangeClient, initialClient, isEdit = false, conf }) {
    const [docType, setDocType] = useState("1"); // Por defecto: VALE

    const [docNumber, setDocNumber] = useState("99999999");
    const [clientData, setClientData] = useState(null);
    const [clientName, setClientName] = useState("");
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [nroTipoDocumento, setNroTipoDocumento] = useState("");
    const [isClientPickerReady, setIsClientPickerReady] = useState(false);
    const prevDocNumberRef = useRef("99999999");
    const initialClientAppliedRef = useRef(false);
    const confAppliedRef = useRef(false);
    const clientDataRef = useRef(null);

    useEffect(() => {
        if (conf?.defaultSellDocument && !confAppliedRef.current && !isEdit) {
            setDocType(String(conf.defaultSellDocument));
            confAppliedRef.current = true;
        }
    }, [conf, isEdit]);

    useEffect(() => {
        if (!isEdit) return; // Solo usar initialClient para inicializar en modo edición

        if (initialClient && initialClient.IdCliente && !initialClientAppliedRef.current) {
            initialClientAppliedRef.current = true;
            clientDataRef.current = initialClient;
            setClientData(initialClient);
            setClientName(initialClient.RazonSocial || initialClient.Nombre || "");
            setNroTipoDocumento(initialClient.NroTipoDocumento || "");

            const clientDocNumber = initialClient.NumeroDocumento || initialClient.NroTipoDocumento || "";
            if (clientDocNumber) {
                setDocNumber(clientDocNumber);
                prevDocNumberRef.current = clientDocNumber;

                const documentForCheck = initialClient.NroTipoDocumento || initialClient.NumeroDocumento || "";
                if (documentForCheck.length === 11) {
                    setDocType("2");

                } else if (clientDocNumber.length === 8 && /^\d{8}$/.test(clientDocNumber) && clientDocNumber !== "99999999") {
                    setDocType("4");
                } else if (clientDocNumber === "99999999" && conf?.defaultSellDocument) {
                    setDocType(String(conf.defaultSellDocument));
                } else {
                    setDocType("1");
                }
            }
            setIsInitialLoad(false);
            setIsClientPickerReady(true);
        }
    }, [initialClient]);

    useEffect(() => {
        if (isEdit) return;

        if (initialClient && initialClient.IdCliente) return;

        setIsClientPickerReady(true);
    }, [initialClient, isEdit]);

    useEffect(() => {
        if (conf && conf.defaultSellDocument && !isEdit && !confAppliedRef.current) {
            confAppliedRef.current = true;
            // Only apply default if docType wasn't forced to FACTURA by initialClient
            if (docType !== "2") {
                setDocType(String(conf.defaultSellDocument));
            }
        }
    }, [conf, isEdit]);

    useEffect(() => {
        clientDataRef.current = clientData;

        if (!clientData) {
            setClientName("");
            setNroTipoDocumento("");
            onChangeClient(null);
            return;
        }

        setClientName(clientData.RazonSocial || clientData.Nombre || "");
        setNroTipoDocumento(clientData.NroTipoDocumento || "");

        onChangeClient({
            ...clientData,
            tipoComprobante: docType,
            nroTipoDocumento: clientData.NroTipoDocumento || ""
        });
    }, [clientData, onChangeClient, docType]);

    const fetchClient = async (numero) => {
        try {
            const req = await fetch(`/api/preVentas/traerCliente/${numero}`);

            if (!req.ok) {
                return null;
            }

            const data = await req.json();

            if (!Array.isArray(data) || data.length === 0) {
                return null;
            }

            return data[0];
        } catch (e) {
            console.error("Error al buscar cliente:", e);
            return null;
        }
    };

    // Efecto para manejar cambios en el número de documento
    useEffect(() => {
        // Evitar procesar en la carga inicial o si no ha cambiado
        if (isInitialLoad || docNumber === prevDocNumberRef.current) return;

        prevDocNumberRef.current = docNumber;

        // LÓGICA DEL PADRE:
        // Cuando el número cambia a 11 dígitos, cambiar automáticamente a FACTURA
        if (docNumber.length === 11) {
            if (docType !== "2") {
                setDocType("2");
            }
        }

        setIsInitialLoad(false);
    }, [docNumber, docType, isInitialLoad]);

    // Manejar cambio MANUAL del tipo de documento
    const handleDocTypeChange = (e) => {
        const newDocType = e.target.value;
        const oldDocType = docType;

        // Actualizar el tipo de documento
        setDocType(newDocType);

        // LÓGICA COPIADA DEL PADRE:
        // Si cambiamos a FACTURA y el número tiene 11 dígitos, buscar cliente
        if (newDocType === "2" && docNumber.length === 11) {
            // Document is valid
        }
        // Si cambiamos de un tipo a otro y el número no es compatible, limpiar cliente
        else if (oldDocType !== newDocType) {
            // Si cambiamos de FACTURA/BOLETA a otro tipo y el número no es compatible
            if ((oldDocType === "2" && newDocType === "4" && docNumber.length !== 8) ||
                (oldDocType === "4" && newDocType === "2" && docNumber.length !== 11)) {
                setClientData(null);
                setClientName("");
            }
        }
    };

    const handleSelectClient = (client) => {
        if (!client) {
            clientDataRef.current = null;
            setClientData(null);
            setClientName("");
            setNroTipoDocumento("");
            setDocNumber("");
            return;
        }

        const clientDocNumber = client.NumeroDocumento || client.NroTipoDocumento || "";

        // Actualizar número de documento
        setDocNumber(clientDocNumber);
        prevDocNumberRef.current = clientDocNumber;

        // LÓGICA DEL PADRE: Solo si tiene 11 dígitos -> FACTURA, sino no tocar
        if (clientDocNumber.length === 11) {
            setDocType("2"); // FACTURA para RUC de 11 dígitos
        }

        // Actualizar datos del cliente
        clientDataRef.current = client;
        setClientData(client);
        setNroTipoDocumento(client.NroTipoDocumento || "");
    };

    const customHandleSelectClient = (client) => {
        const currentClientData = clientDataRef.current;

        if (initialClientAppliedRef.current) {
            if (client?.NumeroDocumento === "99999999" && currentClientData?.NumeroDocumento !== "99999999") {
                return;
            }
            if (client?.IdCliente && currentClientData?.IdCliente && client.IdCliente === currentClientData.IdCliente) {
                return;
            }
        }

        handleSelectClient(client);

        if (client && onChangeClient) {
            onChangeClient({
                ...client,
                tipoComprobante: client.NumeroDocumento?.length === 11 ? "2" : docType,
                nroTipoDocumento: client.NroTipoDocumento || ""
            });
        }
    };

    return (
        <>
            <select
                name="selector"
                className='abed-preventa__tipo--comprobante--select'
                value={docType}
                onChange={handleDocTypeChange}
            >
                <option className='preventa__tipo--comprobante--option' value="1">VALE</option>
                <option className='preventa__tipo--comprobante--option' value="2">FACTURA</option>
                <option className='preventa__tipo--comprobante--option' value="4">BOLETA</option>
            </select>

            {isClientPickerReady && (
                <ClientPicker
                    onSelectClient={customHandleSelectClient}
                    defaultClient={docNumber}
                    className="custom-client-picker"
                />
            )}

            {clientName && (
                <div className='abed-ingresar_cliente'>
                    <span className="fw-bold">{clientName}</span>
                    {clientData?.NumeroDocumento && (
                        <small className="ms-2 text-muted">
                            ({docType === "2" ? "RUC" : docType === "4" ? "DNI" : "VALE"}: {clientData.NumeroDocumento})
                            {nroTipoDocumento && (
                                <span className="ms-2">
                                    (Tipo: {nroTipoDocumento})
                                </span>
                            )}
                        </small>
                    )}
                </div>
            )}
        </>
    );
}